import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import SwedenMap from './SwedenMap';
import { ErrorBoundary } from 'react-error-boundary';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Tab } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';
import { Helmet } from 'react-helmet';

// Error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (
        <div className="p-4 bg-red-500/10 rounded-lg">
            <h2 className="text-xl font-bold mb-2">Something went wrong:</h2>
            <pre className="text-sm text-red-200">{error.message}</pre>
            <button 
                onClick={resetErrorBoundary}
                className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
                Try again
            </button>
        </div>
    );
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    annotationPlugin
);

// Validation helper
const validatePriceData = (data) => {
    if (!data || !data.hourly || !Array.isArray(data.hourly)) {
        return false;
    }
    return true;
};

// Data aggregation functions for zoom levels
const aggregateDataForZoom = (hourlyData, zoomLevel) => {
    if (!hourlyData || !Array.isArray(hourlyData)) return [];
    
    const is15MinData = hourlyData.length > 24;
    
    if (!is15MinData) {
        if (zoomLevel === '60min') return hourlyData;
        if (zoomLevel === '30min') {
            return hourlyData.flatMap(price => [price, price]);
        }
        return hourlyData.flatMap(price => [price, price, price, price]);
    }
    
    // 15-minute data aggregation
    switch (zoomLevel) {
        case '15min':
            return hourlyData;
        case '30min':
            const thirtyMinData = [];
            for (let i = 0; i < hourlyData.length; i += 2) {
                const chunk = hourlyData.slice(i, i + 2).filter(p => p !== null && !isNaN(p));
                if (chunk.length > 0) {
                    thirtyMinData.push(chunk.reduce((sum, price) => sum + price, 0) / chunk.length);
                } else {
                    thirtyMinData.push(null);
                }
            }
            return thirtyMinData;
        case '60min':
            const hourlyAggregated = [];
            for (let i = 0; i < hourlyData.length; i += 4) {
                const chunk = hourlyData.slice(i, i + 4).filter(p => p !== null && !isNaN(p));
                if (chunk.length > 0) {
                    hourlyAggregated.push(chunk.reduce((sum, price) => sum + price, 0) / chunk.length);
                } else {
                    hourlyAggregated.push(null);
                }
            }
            return hourlyAggregated;
        default:
            return hourlyData;
    }
};

const getZoomLevelInfo = (zoomLevel) => {
    switch (zoomLevel) {
        case '15min':
            return { points: 96, interval: 15, labelStep: 4 };
        case '30min':
            return { points: 48, interval: 30, labelStep: 2 };
        case '60min':
            return { points: 24, interval: 60, labelStep: 1 };
        default:
            return { points: 96, interval: 15, labelStep: 4 };
    }
};

const getCurrentPrice = (hourlyPrices, isClient = true, is15Min = false) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    
    if (!isClient) return hourlyPrices[0];
    
    const now = new Date();
    if (is15Min) {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const quarterIndex = Math.floor(currentMinute / 15);
        let position = currentHour * 4 + quarterIndex;
        
        if (position >= hourlyPrices.length) {
            const proportion = (currentHour + currentMinute / 60) / 24;
            position = Math.floor(proportion * hourlyPrices.length);
        }
        
        return hourlyPrices[Math.min(position, hourlyPrices.length - 1)];
    } else {
        const currentHour = now.getHours();
        return hourlyPrices[Math.min(currentHour, hourlyPrices.length - 1)];
    }
};

const getLowestPrice = (hourlyPrices) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    
    const validPrices = hourlyPrices.filter(price => price !== null && price !== undefined && !isNaN(price));
    if (validPrices.length === 0) return null;
    
    const lowest = Math.min(...validPrices);
    const position = hourlyPrices.indexOf(lowest);
    
    return { price: lowest, position };
};

const getHighestPrice = (hourlyPrices) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    
    const validPrices = hourlyPrices.filter(price => price !== null && price !== undefined && !isNaN(price));
    if (validPrices.length === 0) return null;
    
    const highest = Math.max(...validPrices);
    const position = hourlyPrices.indexOf(highest);
    
    return { price: highest, position };
};

const formatHour = (position, is15Min = false) => {
    if (position === null || position === undefined || position < 0) {
        return '--:--';
    }
    
    if (is15Min) {
        if (position >= 96) {
            console.warn(`Invalid 15-min position: ${position}, using modulo`);
            position = position % 96;
        }
        const hour = Math.floor(position / 4);
        const minute = (position % 4) * 15;
        return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    } else {
        if (position >= 24) {
            console.warn(`Invalid hourly position: ${position}, using modulo`);
            position = position % 24;
        }
        return `${String(position).padStart(2, '0')}:00`;
    }
};

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

const ElectricityPricingContent = ({
    defaultRegion = 'SE3',
    regions = ['SE1', 'SE2', 'SE3', 'SE4'],
    regionColors = {
        'SE1': '#59a14f',
        'SE2': '#4e79a7',
        'SE3': '#edc949',
        'SE4': '#e15759'
    },
    getRegionColorClass = (region) => {
        const colors = {
            'SE1': 'bg-green-500',
            'SE2': 'bg-blue-500',
            'SE3': 'bg-yellow-500',
            'SE4': 'bg-red-500'
        };
        return colors[region] || 'bg-gray-500';
    },
    MapComponent = SwedenMap,
    apiEndpoint = '/api/prices',
    countryName = 'Sverige'
}) => {
    const [data, setData] = useState({ today: '', prices: {}, metadata: {}, predictedPrices: { tomorrow: {} } });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(defaultRegion);
    const [animationComplete, setAnimationComplete] = useState(false);
    const [includeVAT, setIncludeVAT] = useState(false);
    const [predictedPrices, setPredictedPrices] = useState({ tomorrow: {} });
    const [selectedDate, setSelectedDate] = useState('today');
    const [isClient, setIsClient] = useState(false);
    const [zoomLevel, setZoomLevel] = useState('15min');

    const applyVAT = useCallback((price) => {
        if (price === null || price === undefined || isNaN(price)) return null;
        const numPrice = Number(price);
        if (isNaN(numPrice)) return null;
        return includeVAT ? (numPrice * 1.25) : numPrice;
    }, [includeVAT]);

    // Helper function to check if we should show the "tomorrow prices update" message
    const shouldShowTomorrowUpdateMessage = useCallback(() => {
        if (!isClient || selectedDate !== 'tomorrow') return false;
        
        const now = new Date();
        const cetTime = new Date(now.toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' }));
        const hour = cetTime.getHours();
        const minute = cetTime.getMinutes();
        
        // Show message only before 13:05
        const isBefore1305 = hour < 13 || (hour === 13 && minute < 5);
        
        // Check if tomorrow's prices are available
        const tomorrowPrices = data.predictedPrices?.tomorrow || {};
        const hasTomorrowPrices = Object.keys(tomorrowPrices).length > 0 && 
            Object.values(tomorrowPrices).some(regionData => 
                regionData?.hourly && regionData.hourly.length > 0
            );
        
        return isBefore1305 && !hasTomorrowPrices;
    }, [isClient, selectedDate, data.predictedPrices]);

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        const fetchPricesWithRetry = async (retryCount = 0) => {
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}${apiEndpoint}`);
                
                // Normalize the data structure to match API response
                const normalizedData = {
                    today: response.data.today || '',
                    metadata: response.data.metadata || {},
                    prices: {},
                    predictedPrices: response.data.predictedPrices || { tomorrow: {} }
                };

                // Handle both Swedish and Norwegian data structures
                if (response.data.prices) {
                    // Swedish structure - convert [area, data] arrays to objects
                    Object.entries(response.data.prices).forEach(([area, priceArray]) => {
                        if (Array.isArray(priceArray) && priceArray.length === 2) {
                            // If it's in [area, data] format from API
                            normalizedData.prices[priceArray[0]] = priceArray[1];
                        } else {
                            // Already in correct format
                            normalizedData.prices[area] = priceArray;
                        }
                    });
                } else if (response.data) {
                    // Norwegian structure - assuming direct region data
                    normalizedData.prices = response.data;
                }

                setData(normalizedData);
                setPredictedPrices(normalizedData.predictedPrices);
                setError(null);
                window.dispatchEvent(new Event('electricityPricingLoaded'));
            } catch (err) {
                console.error(`Attempt ${retryCount + 1} failed:`, err);
                if (retryCount < MAX_RETRIES) {
                    setTimeout(() => fetchPricesWithRetry(retryCount + 1), RETRY_DELAY);
                } else {
                    setError('Could not load electricity prices. Please try again later.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchPricesWithRetry();
    }, [apiEndpoint]);

    if (error) return <div>{error}</div>;

    const generateChartData = (prices) => {
        // Determine if we're dealing with 15-minute data
        const firstRegionData = Object.values(prices)[0];
        
        // Check multiple indicators for 15-minute resolution
        const is15Min = firstRegionData?.resolution === 'PT15M' || 
                       firstRegionData?.is15Min === true || 
                       (firstRegionData?.dataPoints && firstRegionData.dataPoints === 96) ||
                       (firstRegionData?.hourly && firstRegionData.hourly.length === 96);
        
        // Get zoom level configuration
        const zoomInfo = getZoomLevelInfo(zoomLevel);
        
        // Generate labels based on zoom level
        const timeLabels = Array.from({ length: zoomInfo.points }, (_, i) => {
            const totalMinutes = i * zoomInfo.interval;
            const hour = Math.floor(totalMinutes / 60);
            const minute = totalMinutes % 60;
            
            if (hour >= 24) return '';
            
            return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        });
        
        const currentHour = isClient ? new Date().getHours() : 0;
        const currentMinute = isClient ? new Date().getMinutes() : 0;
        
        const getCurrentPosition = () => {
            if (!isClient) return 0;
            
            const totalCurrentMinutes = currentHour * 60 + currentMinute;
            return Math.floor(totalCurrentMinutes / zoomInfo.interval);
        };
        
        const currentPosition = getCurrentPosition();
        
        const datasets = Object.entries(prices).map(([region, data]) => {
            if (!validatePriceData(data)) {
                console.error(`Invalid data structure for region ${region}:`, data);
                return {
                    label: region,
                    data: Array(24).fill(null),
                    borderColor: regionColors[region] || '#ffffff',
                    backgroundColor: regionColors[region] + '20',
                    fill: true,
                };
            }

            const aggregatedData = aggregateDataForZoom(data.hourly, zoomLevel);
            
            const paddedData = [...aggregatedData];
            while (paddedData.length < zoomInfo.points) {
                paddedData.push(null);
            }
            if (paddedData.length > zoomInfo.points) {
                paddedData.length = zoomInfo.points;
            }

            return {
                label: region,
                data: paddedData.map(price => price !== null ? applyVAT(price) : null),
                borderColor: regionColors[region],
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    
                    if (!chartArea) return regionColors[region] + '20';

                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, `${regionColors[region]}40`);
                    gradient.addColorStop(1, `${regionColors[region]}00`);
                    return gradient;
                },
                fill: 'start',
                stepped: 'before',
                tension: 0.4,
            };
        });

        return { labels: timeLabels, datasets, is15Min, zoomLevel, currentPosition };
    };

    const getCurrentDataset = () => {
        switch (selectedDate) {
            case 'tomorrow':
                return predictedPrices?.tomorrow || {};
            default:
                return data.prices;
        }
    };

    const getFormattedDate = () => {
        const date = new Date();
        switch (selectedDate) {
            case 'tomorrow':
                date.setDate(date.getDate() + 1);
                break;
            default:
                break;
        }
        return date.toLocaleDateString('sv-SE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        resizeDelay: 100,
        events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            annotation: {
                annotations: {
                    currentTime: {
                        display: selectedDate === 'today' && isClient,
                        type: 'line',
                        xMin: (() => {
                            if (!isClient) return 0;
                            const now = new Date();
                            const totalCurrentMinutes = now.getHours() * 60 + now.getMinutes();
                            const zoomInfo = getZoomLevelInfo(zoomLevel);
                            const position = Math.floor(totalCurrentMinutes / zoomInfo.interval);
                            return Math.min(position, zoomInfo.points - 1);
                        })(),
                        xMax: (() => {
                            if (!isClient) return 0;
                            const now = new Date();
                            const totalCurrentMinutes = now.getHours() * 60 + now.getMinutes();
                            const zoomInfo = getZoomLevelInfo(zoomLevel);
                            const position = Math.floor(totalCurrentMinutes / zoomInfo.interval);
                            return Math.min(position, zoomInfo.points - 1);
                        })(),
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        borderWidth: 2,
                        borderDash: [6, 6],
                        label: {
                            display: true,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            content: (ctx) => {
                                const regionData = data.prices[selectedRegion];
                                const is15Min = regionData?.resolution === 'PT15M' || 
                                               regionData?.is15Min || 
                                               regionData?.dataPoints === 96;
                                const currentPrice = getCurrentPrice(regionData?.hourly, isClient, is15Min);
                                const appliedPrice = applyVAT(currentPrice);
                                return [`Nu`, `${appliedPrice !== null ? appliedPrice.toFixed(2) : '-'} öre/kWh`];
                            },
                            position: 'start',
                            padding: 6,
                            font: {
                                size: 12
                            }
                        }
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                titleFont: {
                    size: 14,
                    family: 'system-ui'
                },
                bodyFont: {
                    size: 13,
                    family: 'system-ui'
                },
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                callbacks: {
                    label: function(tooltipItem) {
                        const zoomInfo = getZoomLevelInfo(zoomLevel);
                        
                        const startMinutes = tooltipItem.dataIndex * zoomInfo.interval;
                        const endMinutes = startMinutes + zoomInfo.interval;
                        
                        const startHour = Math.floor(startMinutes / 60);
                        const startMin = startMinutes % 60;
                        const endHour = Math.floor(endMinutes / 60);
                        const endMin = endMinutes % 60;
                        
                        let timeRange = '';
                        let isCurrentPeriod = false;
                        
                        if (startHour < 24) {
                            timeRange = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}–${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;
                            
                            if (isClient) {
                                const now = new Date();
                                const currentMinutes = now.getHours() * 60 + now.getMinutes();
                                isCurrentPeriod = currentMinutes >= startMinutes && currentMinutes < endMinutes;
                            }
                        } else {
                            timeRange = 'Invalid time';
                        }
                        
                        const appliedPrice = applyVAT(tooltipItem.raw);
                        let label = `${tooltipItem.dataset.label}: ${appliedPrice !== null ? appliedPrice.toFixed(2) : '-'} öre/kWh`;
                        if (isCurrentPeriod) {
                            label += ' (Aktuellt pris)';
                        }
                        return `${label} (${timeRange})`;
                    },
                    title: function(tooltipItems) {
                        const item = tooltipItems[0];
                        const zoomInfo = getZoomLevelInfo(zoomLevel);
                        
                        if (isClient) {
                            const now = new Date();
                            const currentMinutes = now.getHours() * 60 + now.getMinutes();
                            const periodStart = item.dataIndex * zoomInfo.interval;
                            const periodEnd = periodStart + zoomInfo.interval;
                            
                            if (currentMinutes >= periodStart && currentMinutes < periodEnd) {
                                return `${item.label} (Aktuellt pris)`;
                            }
                        }
                        
                        return item.label;
                    }
                }
            },
            legend: {
                position: 'top',
                align: 'start',
                labels: {
                    color: '#ffffff',
                    padding: 20,
                    font: {
                        family: 'system-ui',
                        size: 13
                    },
                    usePointStyle: true,
                    pointStyle: 'rect'
                }
            }
        },
        scales: {
            y: {
                border: {
                    display: false
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.05)',
                    drawTicks: false
                },
                ticks: {
                    color: 'rgba(255, 255, 255, 0.7)',
                    font: {
                        size: 12,
                        family: 'system-ui'
                    },
                    padding: 10,
                    callback: function(value) {
                        return value.toFixed(0) + ' öre';
                    }
                }
            },
            x: {
                border: {
                    display: false
                },
                grid: {
                    display: false
                },
                ticks: {
                    color: 'rgba(255, 255, 255, 0.7)',
                    font: {
                        size: 12,
                        family: 'system-ui'
                    },
                    padding: 10,
                    callback: function(value, index) {
                        const zoomInfo = getZoomLevelInfo(zoomLevel);
                        
                        if (index % zoomInfo.labelStep === 0) {
                            const totalMinutes = index * zoomInfo.interval;
                            const hour = Math.floor(totalMinutes / 60);
                            const minute = totalMinutes % 60;
                            
                            if (hour < 24) {
                                return minute === 0 ? `${hour}:00` : `${hour}:${minute.toString().padStart(2, '0')}`;
                            }
                        }
                        return '';
                    }
                }
            }
        }
    };

    const MemoizedChart = React.memo(({ data, options }) => (
        <Line data={data} options={options} redraw={false} />
    ));

    const handleRegionSelect = (region) => {
        setSelectedRegion(region);
    };

    const renderChart = () => {
        try {
            // Check if we should show the tomorrow update message
            if (shouldShowTomorrowUpdateMessage()) {
                return (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <div className="text-lg font-semibold mb-2">
                                Morgondagens priser uppdateras klockan 13:05
                            </div>
                            <div className="text-sm text-white/60">
                                Kom tillbaka efter 13:05 för att se morgondagens elpriser
                            </div>
                        </div>
                    </div>
                );
            }

            const chartData = generateChartData(
                selectedRegion === 'ALL' 
                    ? getCurrentDataset() 
                    : { [selectedRegion]: getCurrentDataset()[selectedRegion] }
            );

            if (!chartData || !chartData.datasets || !chartData.labels) {
                console.error('Invalid chart data:', chartData);
                return <div>Error: Invalid data format</div>;
            }

            return (
                <MemoizedChart 
                    data={chartData} 
                    options={chartOptions}
                />
            );
        } catch (error) {
            console.error('Chart rendering error:', error);
            return <div>Error rendering chart</div>;
        }
    };

    return (
        <div className="min-h-screen overflow-y-auto relative bg-gradient-to-r from-purple1 via-purple2 to-purple4 text-white py-8 md:py-12">
            <div className="container mx-auto px-4 md:px-8">                
                {regions.length === 1 ? (
                    <div className="mb-8">
                        <div className="w-full">
                            {!loading && Object.entries(data.prices)
                                .filter(([area]) => regions.includes(area))
                                .map(([area, priceData]) => (
                                <div 
                                    key={area} 
                                    className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-4 sm:p-6"
                                >
                                    <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(area)} mb-4`}></div>
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-xl sm:text-2xl font-bold">{area}</h3>
                                        <div className="text-right">
                                            <p className="text-2xl sm:text-4xl font-extrabold">
                                                {(() => {
                                                    const price = applyVAT(parseFloat(priceData.average));
                                                    return price !== null ? price.toFixed(2) : '-';
                                                })()}
                                                <span className="text-lg sm:text-xl opacity-70 ml-2">öre/kWh</span>
                                            </p>
                                            <p className="text-sm text-white/70 mt-1">Genomsnittspris idag</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className="animate-pulse bg-white/5 rounded-lg h-[100px] sm:h-[120px]" />
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-12">
                        <div>
                            <div className="grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-4">
                                {!loading && Object.entries(data.prices)
                                    .filter(([area]) => regions.includes(area))
                                    .map(([area, priceData]) => (
                                    <div 
                                        key={area} 
                                        className={`bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-2 sm:p-4 cursor-pointer 
                                            transform transition-all hover:-translate-y-1 hover:scale-102 hover:shadow-xl
                                            ${selectedRegion === area ? 'ring-2 ring-white/30' : ''}`}
                                        onClick={() => handleRegionSelect(area)}
                                    >
                                        <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(area)}`}></div>
                                        <div className="text-center">
                                            <h3 className="text-base sm:text-lg font-bold">{area}</h3>
                                            <p className="text-xl sm:text-2xl font-extrabold mt-0.5 sm:mt-1">
                                                {(() => {
                                                    const price = applyVAT(parseFloat(priceData.average));
                                                    return price !== null ? price.toFixed(2) : '-';
                                                })()}
                                                <span className="text-sm sm:text-base opacity-70 ml-1">öre</span>
                                            </p>
                                        </div>
                                    </div>
                                ))}
                                {loading && Array(regions.length).fill(0).map((_, index) => (
                                    <div key={index} className="animate-pulse bg-white/5 rounded-lg h-[80px] sm:h-[120px]" />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div className={`grid gap-6 ${MapComponent ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'}`}>
                    <div className={`space-y-6 ${MapComponent ? 'lg:col-span-2' : ''}`}>
                        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
                                <div className="mb-4 md:mb-0">
                                    <h2 className="text-2xl font-bold">{getFormattedDate()}</h2>
                                    {!loading && (
                                        <div className="flex items-center mt-2 text-sm text-white/70">
                                            <div className={`w-2 h-2 rounded-full mr-2 ${
                                                zoomLevel === '15min' ? 'bg-green-400' : 
                                                zoomLevel === '30min' ? 'bg-yellow-400' : 'bg-blue-400'
                                            }`}></div>
                                            {zoomLevel === '15min' && '15-minuters intervaller'}
                                            {zoomLevel === '30min' && '30-minuters intervaller'}
                                            {zoomLevel === '60min' && '60-minuters intervaller'}
                                            {data.metadata?.resolution === 'PT15M' || data.metadata?.is15MinResolution ? 
                                                ` (${getZoomLevelInfo(zoomLevel).points} visade punkter)` : 
                                                ` (${getZoomLevelInfo(zoomLevel).points} datapunkter)`
                                            }
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col md:flex-row items-start md:items-center space-y-3 md:space-y-0 md:space-x-4 w-full md:w-auto">
                                    <div className="flex items-center space-x-2 justify-center md:justify-start w-full md:w-auto">
                                        <span className="text-sm text-white/70">Visa priser</span>
                                        <Switch
                                            checked={includeVAT}
                                            onChange={setIncludeVAT}
                                            className={`${
                                                includeVAT ? 'bg-purple-500' : 'bg-white/10'
                                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
                                        >
                                            <span className="sr-only">Visa priser med moms</span>
                                            <span
                                                className={`${
                                                    includeVAT ? 'translate-x-6' : 'translate-x-1'
                                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                            />
                                        </Switch>
                                        <span className="text-sm text-white/70">{includeVAT ? 'inkl. moms' : 'exkl. moms'}</span>
                                    </div>
                                    
                                    <div className="flex justify-between md:justify-start items-center gap-3 md:gap-4 w-full md:w-auto">
                                        <div className="flex space-x-2">
                                            <button 
                                                onClick={() => setSelectedDate('today')}
                                                className={`px-3 py-2 text-xs rounded-md font-medium ${
                                                    selectedDate === 'today' ? 'bg-white/20' : 'bg-white/5'
                                                }`}
                                            >
                                                Idag
                                            </button>
                                            <button 
                                                onClick={() => setSelectedDate('tomorrow')}
                                                className={`px-3 py-2 text-xs rounded-md font-medium ${
                                                    selectedDate === 'tomorrow' ? 'bg-white/20' : 'bg-white/5'
                                                }`}
                                            >
                                                Imorgon
                                            </button>
                                        </div>
                                        
                                        <div className="flex space-x-2">
                                            <button 
                                                onClick={() => setZoomLevel('15min')}
                                                className={`px-2 py-1 text-xs rounded-md font-medium ${
                                                    zoomLevel === '15min' ? 'bg-green-500/20 text-green-300' : 'bg-white/5'
                                                }`}
                                            >
                                                15min
                                            </button>
                                            <button 
                                                onClick={() => setZoomLevel('30min')}
                                                className={`px-2 py-1 text-xs rounded-md font-medium ${
                                                    zoomLevel === '30min' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-white/5'
                                                }`}
                                            >
                                                30min
                                            </button>
                                            <button 
                                                onClick={() => setZoomLevel('60min')}
                                                className={`px-2 py-1 text-xs rounded-md font-medium ${
                                                    zoomLevel === '60min' ? 'bg-blue-500/20 text-blue-300' : 'bg-white/5'
                                                }`}
                                            >
                                                1h
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="h-96 w-full">
                                {loading ? (
                                    <div className="flex items-center justify-center h-full">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                                    </div>
                                ) : (
                                    <ErrorBoundary 
                                        FallbackComponent={ErrorFallback}
                                        onReset={() => window.location.reload()}
                                    >
                                        {renderChart()}
                                    </ErrorBoundary>
                                )}
                            </div>
                        </div>

                        {/* Price Statistics Cards - moved below chart */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Current Price Card */}
                            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4">
                                <h4 className="text-sm font-semibold text-blue-300 mb-2">Aktuellt pris - {selectedRegion}</h4>
                                {loading ? (
                                    <Skeleton height={30} baseColor="#ffffff20" highlightColor="#ffffff40" />
                                ) : (
                                    <div>
                                        <div className="text-xl font-bold">
                                            {(() => {
                                                const regionData = getCurrentDataset()[selectedRegion];
                                                if (!regionData) return '-';
                                                
                                                const is15Min = regionData.resolution === 'PT15M' || 
                                                               regionData.is15Min || 
                                                               regionData.dataPoints === 96;
                                                const currentPrice = getCurrentPrice(regionData.hourly, isClient, is15Min);
                                                const appliedPrice = applyVAT(currentPrice);
                                                return appliedPrice !== null ? appliedPrice.toFixed(2) : '-';
                                            })()}
                                            <span className="text-sm opacity-70 ml-1">öre/kWh</span>
                                        </div>
                                        <div className="text-xs text-white/60">
                                            {isClient ? `${new Date().getHours().toString().padStart(2, '0')}:${new Date().getMinutes().toString().padStart(2, '0')}` : 'Nu'}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Lowest Price */}
                            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4">
                                <h4 className="text-sm font-semibold text-green-300 mb-2">Lägsta pris idag</h4>
                                {loading ? (
                                    <Skeleton height={30} baseColor="#ffffff20" highlightColor="#ffffff40" />
                                ) : (
                                    <div>
                                        {(() => {
                                            const regionData = getCurrentDataset()[selectedRegion];
                                            if (!regionData) return <span>-</span>;
                                            
                                            const is15Min = regionData.resolution === 'PT15M' || 
                                                           regionData.is15Min || 
                                                           regionData.dataPoints === 96;
                                            const lowest = getLowestPrice(regionData.hourly);
                                            if (!lowest) return <span>-</span>;
                                            
                                            const appliedPrice = applyVAT(lowest.price);
                                            return (
                                                <div>
                                                    <div className="text-xl font-bold">
                                                        {appliedPrice !== null ? appliedPrice.toFixed(2) : '-'}
                                                        <span className="text-sm opacity-70 ml-1">öre</span>
                                                    </div>
                                                    <div className="text-xs text-white/60">
                                                        {formatHour(lowest.position, is15Min)}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>

                            {/* Highest Price */}
                            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4">
                                <h4 className="text-sm font-semibold text-red-300 mb-2">Högsta pris idag</h4>
                                {loading ? (
                                    <Skeleton height={30} baseColor="#ffffff20" highlightColor="#ffffff40" />
                                ) : (
                                    <div>
                                        {(() => {
                                            const regionData = getCurrentDataset()[selectedRegion];
                                            if (!regionData) return <span>-</span>;
                                            
                                            const is15Min = regionData.resolution === 'PT15M' || 
                                                           regionData.is15Min || 
                                                           regionData.dataPoints === 96;
                                            const highest = getHighestPrice(regionData.hourly);
                                            if (!highest) return <span>-</span>;
                                            
                                            const appliedPrice = applyVAT(highest.price);
                                            return (
                                                <div>
                                                    <div className="text-xl font-bold">
                                                        {appliedPrice !== null ? appliedPrice.toFixed(2) : '-'}
                                                        <span className="text-sm opacity-70 ml-1">öre</span>
                                                    </div>
                                                    <div className="text-xs text-white/60">
                                                        {formatHour(highest.position, is15Min)}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>

                            {/* Average Price */}
                            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4">
                                <h4 className="text-sm font-semibold text-blue-300 mb-2">Genomsnittspris</h4>
                                {loading ? (
                                    <Skeleton height={30} baseColor="#ffffff20" highlightColor="#ffffff40" />
                                ) : (
                                    <div className="text-xl font-bold">
                                        {(() => {
                                            const regionData = getCurrentDataset()[selectedRegion];
                                            if (!regionData || !regionData.average) return '-';
                                            
                                            const appliedPrice = applyVAT(parseFloat(regionData.average));
                                            return appliedPrice !== null ? appliedPrice.toFixed(2) : '-';
                                        })()}
                                        <span className="text-sm opacity-70 ml-1">öre/kWh</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <div className="space-y-6">
                        {/* Map Component - moved to right side */}
                        {MapComponent && (
                            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
                                <h3 className="text-lg font-bold mb-4">Elområden</h3>
                                <MapComponent
                                    data={getCurrentDataset()}
                                    selectedRegion={selectedRegion}
                                    onRegionSelect={handleRegionSelect}
                                    applyVAT={applyVAT}
                                    loading={loading}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main component wrapper with error boundary
const ElectricityPricing = (props) => {
    return (
        <>
            <Helmet>
                <title>Elpriser i Sverige - Dagens Elpriser</title>
                <meta name="description" content="Se aktuella elpriser för alla svenska elområden. Jämför priser mellan SE1, SE2, SE3 och SE4 med realtidsdata." />
                <meta name="keywords" content="elpriser, sverige, SE1, SE2, SE3, SE4, elektricitet, energi" />
            </Helmet>
            <ErrorBoundary 
                FallbackComponent={ErrorFallback}
                onReset={() => window.location.reload()}
            >
                <ElectricityPricingContent {...props} />
            </ErrorBoundary>
        </>
    );
};

export default ElectricityPricing;